import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import styles from "./VerifyEmailLanding.module.css";
import { emitEmailVerified } from "../../lib/emailVerificationEvents";
import { AUTH_API_URL, authFetch } from "../../lib/authFetch";

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "support@drivenow.it.com";

type ResendState =
  | { status: "idle" }
  | { status: "sending" }
  | { status: "sent" }
  | { status: "error"; message: string };

type VerifyResult =
  | { status: "idle" | "loading" }
  | { status: "success"; email: string; alreadyVerified: boolean }
  | { status: "error"; message: string };

type Props = {
  /**
   * Caller passes the currently-stored JWT (may be empty) so we can show the
   * right call-to-action after success: logged-in users get "Continue" while
   * logged-out users get "Sign in". The page itself doesn't require a token —
   * the magic-link token in the URL is the sole proof of ownership.
   */
  hasToken: boolean;
};

/**
 * Landing page for magic-link email verification.
 *
 * The URL is the email recipient's first touch after signup, so this page
 * must render cleanly whether the user is logged in or not, and must cope
 * gracefully with missing/bad tokens, expired tokens, and link pre-fetching
 * by email clients (which is why the token is consumed via a POST from this
 * page rather than a side-effect in the link target).
 */
const VerifyEmailLandingPage: React.FC<Props> = ({ hasToken }) => {
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get("token") || "";
  const [result, setResult] = useState<VerifyResult>({
    status: urlToken ? "loading" : "idle",
  });
  const [resend, setResend] = useState<ResendState>({ status: "idle" });
  // Guard against React StrictMode's double-invoke in dev: only POST once per
  // mount. Without this, a valid token would be consumed on the first render,
  // and the second render would see "already used" and flip to an error.
  const consumedRef = useRef(false);

  useEffect(() => {
    if (!urlToken) return;
    if (consumedRef.current) return;
    consumedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${AUTH_API_URL}/verify-email/by-link`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: urlToken }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok) {
          // Tell the always-mounted banner to refetch so a logged-in user's
          // gated UI unlocks immediately (cache would otherwise be stale).
          emitEmailVerified();
          setResult({
            status: "success",
            email: data?.email || "",
            alreadyVerified: Boolean(data?.already_verified),
          });
        } else {
          setResult({
            status: "error",
            message:
              data?.detail ||
              "We couldn't verify this link. It may have expired or already been used.",
          });
        }
      } catch {
        if (cancelled) return;
        setResult({
          status: "error",
          message:
            "We couldn't reach the verification service. Please check your connection and try again.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [urlToken]);

  // Logged-in users whose link expired/was used can request a fresh one right
  // here instead of bouncing back to sign in. The stored JWT authorizes it.
  const handleResend = async () => {
    const jwt = typeof window !== "undefined" ? window.localStorage.getItem("token") : null;
    if (!jwt) return;
    setResend({ status: "sending" });
    try {
      const res = await authFetch(`${AUTH_API_URL}/resend-verification-pin`, {
        method: "POST",
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setResend({
          status: "error",
          message:
            data?.detail || "Too many requests. Please wait a few minutes and try again.",
        });
      } else if (res.ok && data?.email_sent === false) {
        setResend({
          status: "error",
          message: `We're having trouble sending the email. Please contact ${SUPPORT_EMAIL}.`,
        });
      } else if (res.ok) {
        setResend({ status: "sent" });
      } else {
        setResend({
          status: "error",
          message: data?.detail || "Couldn't send a new link. Please try again.",
        });
      }
    } catch {
      setResend({
        status: "error",
        message: "We couldn't reach the verification service. Please try again.",
      });
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <Link to="/" className={styles.brand}>DriveNow</Link>
        <div className={styles.tagline}>Peer-to-peer car marketplace</div>

        {result.status === "idle" && (
          <>
            <div className={`${styles.iconWrap} ${styles.error}`}>
              <ErrorOutlineIcon fontSize="large" />
            </div>
            <h1 className={styles.title}>No verification token</h1>
            <p className={styles.body}>
              This page is meant to be opened from the verification email we
              sent you. If you just signed up and didn't get the email, you
              can request a new one after logging in.
            </p>
            <div className={styles.actions}>
              <Link to="/login" className={styles.primaryButton}>Go to sign in</Link>
              <Link to="/signup" className={styles.secondaryButton}>Create an account</Link>
            </div>
          </>
        )}

        {result.status === "loading" && (
          <>
            <div className={`${styles.iconWrap} ${styles.loading}`}>
              <div className={styles.spinner} aria-label="Verifying" />
            </div>
            <h1 className={styles.title}>Verifying your email…</h1>
            <p className={styles.body}>Hang tight, this only takes a second.</p>
          </>
        )}

        {result.status === "success" && (
          <>
            <div className={`${styles.iconWrap} ${styles.success}`}>
              <CheckCircleOutlineIcon fontSize="large" />
            </div>
            <h1 className={styles.title}>
              {result.alreadyVerified ? "You're all set" : "Email verified"}
            </h1>
            <p className={styles.body}>
              {result.email ? (
                <>
                  <span className={styles.email}>{result.email}</span> is
                  confirmed. You can now create listings, message sellers,
                  and leave reviews.
                </>
              ) : (
                <>
                  Your email is confirmed. You can now create listings,
                  message sellers, and leave reviews.
                </>
              )}
            </p>
            <div className={styles.actions}>
              {hasToken ? (
                <Link to="/" className={styles.primaryButton}>Continue to DriveNow</Link>
              ) : (
                <>
                  <Link to="/login" className={styles.primaryButton}>Sign in</Link>
                  <Link to="/" className={styles.secondaryButton}>Back to home</Link>
                </>
              )}
            </div>
          </>
        )}

        {result.status === "error" && (
          <>
            <div className={`${styles.iconWrap} ${styles.error}`}>
              <ErrorOutlineIcon fontSize="large" />
            </div>
            <h1 className={styles.title}>Verification failed</h1>
            <p className={styles.body}>{result.message}</p>
            {hasToken && resend.status === "sent" ? (
              <p className={styles.body}>
                We sent a new verification email. Check your inbox and open the
                latest link.
              </p>
            ) : null}
            {hasToken && resend.status === "error" ? (
              <p className={`${styles.body} ${styles.errorText}`}>{resend.message}</p>
            ) : null}
            <div className={styles.actions}>
              {hasToken ? (
                <>
                  {resend.status !== "sent" && (
                    <button
                      type="button"
                      className={styles.primaryButton}
                      onClick={handleResend}
                      disabled={resend.status === "sending"}
                    >
                      {resend.status === "sending"
                        ? "Sending…"
                        : "Resend verification email"}
                    </button>
                  )}
                  <Link to="/" className={styles.secondaryButton}>
                    Back to DriveNow
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" className={styles.primaryButton}>
                    Sign in to request a new link
                  </Link>
                  <Link to="/signup" className={styles.secondaryButton}>
                    Create an account
                  </Link>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailLandingPage;
